import { useEffect, useRef } from 'react';
import { X } from 'lucide-react';
import QuizModule from './QuizModule';
import { trackQuizStart, trackQuizAbandon } from '@/lib/ga4-tracking';

interface QuizModalProps {
  isOpen: boolean;
  onClose: () => void;
  onStartCure?: (quantity: 1 | 2 | 4) => void;
  onShowResults?: (profileSlug: string, tags: string[], labels: string[], cureQuantity: 1 | 2 | 4) => void;
}

export function QuizModal({ isOpen, onClose, onStartCure, onShowResults }: QuizModalProps) {
  const quizStartedRef = useRef(false);
  const quizCompletedRef = useRef(false);
  
  // Track quiz_start when modal opens
  useEffect(() => {
    if (isOpen && !quizStartedRef.current) {
      quizStartedRef.current = true;
      quizCompletedRef.current = false;
      const path = window.location.pathname;
      const source = path === '/' || path === '' ? 'homepage' : path.replace(/^\//, '').replace(/\//g, '_');
      trackQuizStart(source);
    }
    if (!isOpen) {
      quizStartedRef.current = false;
    }
  }, [isOpen]);
  
  // Handle close with abandon tracking
  const handleClose = () => {
    if (!quizCompletedRef.current) {
      trackQuizAbandon(0, 10);
    }
    onClose();
  };
  
  // Prevent body scroll and preserve scroll position when modal is open
  useEffect(() => {
    if (isOpen) {
      const scrollY = window.scrollY;
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;
      document.body.style.width = '100%';
      document.body.style.overflow = 'hidden';
      
      return () => {
        document.body.style.position = '';
        document.body.style.top = '';
        document.body.style.width = '';
        document.body.style.overflow = '';
        window.scrollTo(0, scrollY);
      };
    }
  }, [isOpen]);
  
  if (!isOpen) return null;

  const handleComplete = (profile: any, score: number, tags: string[]) => {
    quizCompletedRef.current = true;
    console.log('Quiz completed:', profile.slug, score);
  };

  const handleStartCure = (quantity: 1 | 2 | 4) => {
    // Close quiz modal and trigger order popup with recommended quantity
    onClose();
    if (onStartCure) {
      // Small delay to let the quiz modal close animation finish
      setTimeout(() => {
        onStartCure(quantity);
      }, 100);
    }
  };

  const handleShowResults = (profileSlug: string, tags: string[], labels: string[], cureQuantity: 1 | 2 | 4) => {
    quizCompletedRef.current = true;
    // Close the modal first
    onClose();
    // Then show results on the PDP page with a small delay for modal close animation
    if (onShowResults) {
      setTimeout(() => {
        onShowResults(profileSlug, tags, labels, cureQuantity);
      }, 150);
    }
  };

  return (
    <div 
      className="fixed inset-0 z-[9999] bg-black/50 backdrop-blur-sm flex items-center justify-center p-0 sm:p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="quiz-modal-title"
      onClick={handleClose}
    >
      <div 
        className="w-full max-w-2xl max-h-[90vh] overflow-y-auto bg-white rounded-none sm:rounded-2xl shadow-2xl relative"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close Button */}
        <button
          onClick={handleClose}
          className="absolute top-3 right-3 sm:top-4 sm:right-4 z-10 w-9 h-9 sm:w-10 sm:h-10 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center transition-colors"
          aria-label="Close quiz"
        >
          <X className="w-4 h-4 sm:w-5 sm:h-5" />
        </button>

        {/* Content - Unified white background container wrapping quiz */}
        <div className="p-4 sm:p-6 md:p-8">
          <QuizModule 
            onComplete={handleComplete} 
            onStartCure={handleStartCure}
            onClose={handleClose}
            onShowResults={handleShowResults}
          />
        </div>
      </div>
    </div>
  );
}
